import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  tap,
  throwError,
} from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';

import {
  ICheckEmail,
  IHsCode,
  IHsResponse,
  IIsri,
  IIsriResponse,
  IMonitoringsCode,
  IMonitoringsCodeResponse,
  IPollutionAbatsCodes,
  IPollutionAbatsCodesResponse,
  IPreproCodes,
  IPreproCodesResponse,
  IRefreshToken,
  IUser,
} from '../interfaces/global.interface';
import { environment } from '../../../environments/environment';
import { UrlService } from './url.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomSnackbarComponent } from '../components/custom-snackbar/custom-snackbar.component';

@Injectable({
  providedIn: 'root',
})
export class GlobalService extends UrlService {
  private userStr = sessionStorage.getItem('user');
  readonly user$ = new BehaviorSubject<IUser | null>(
    this.userStr ? JSON.parse(this.userStr) : null,
  );
  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
  ) {
    super();
    this.getUserWhoAmI();
  }
  private _isEmailVerify: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  get isEnabled(): boolean {
    return this._isEmailVerify.value;
  }
  set isEnabled(value: boolean) {
    if (typeof value === 'boolean') {
      this._isEmailVerify.next(value);
    } else {
      console.warn('Invalid value. Expected a boolean.');
    }
  }
  isEnabled$ = this._isEmailVerify.asObservable();

  private userData: {
    fullName?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    mobile?: string;
  } = {};
  setUserData(data: {
    fullName: string;
    email: string;
    password: string;
    confirmPassword?: string;
    mobile: string;
  }) {
    this.userData = data;
  }
  getUserData() {
    return this.userData;
  }

  getUserWhoAmI() {
    const token = sessionStorage.getItem('accessToken');
    if (token) {
      this.whoAmI(`${token}`).subscribe({
        next: (result) => {
          this.userStr = JSON.stringify(result.body);
          sessionStorage.setItem('user', this.userStr);
          this.user$.next(result.body);
        },
        error: (error) => {
          console.error(error);
        },
      });
    }
  }

  isriCode(subcategoryId: number) {
    return this.http.get<IIsriResponse | IIsri[]>(
      this.isriURL + `&filters[subcategory.id]=${subcategoryId}`,
      {
        observe: 'response',
      },
    );
  }
  hsCode(subcategoryId: number) {
    return this.http.get<IHsResponse | IHsCode[]>(
      this.hsValuesURL + `&filters[subcategory.id]=${subcategoryId}`,
      {
        observe: 'response',
      },
    );
  }
  preProcessingCode() {
    return this.http.get<IPreproCodesResponse | IPreproCodes[]>(
      this.preprocessValuesURL,
      {
        observe: 'response',
      },
    );
  }
  monitoringsCode() {
    return this.http.get<IMonitoringsCodeResponse | IMonitoringsCode[]>(
      this.monitoringValuesURL,
      {
        observe: 'response',
      },
    );
  }
  pollutionAbatsCode() {
    return this.http.get<IPollutionAbatsCodesResponse | IPollutionAbatsCodes[]>(
      this.pollutionValuesURL,
      {
        observe: 'response',
      },
    );
  }
  onRefreshToken(): Observable<IRefreshToken> {
    return this.http
      .put<IRefreshToken>(environment.api + 'accounts/refresh-token', {
        refreshToken: sessionStorage.getItem('refreshToken'),
      })
      .pipe(
        tap((data: IRefreshToken) => {
          sessionStorage.setItem('accessToken', data.accessToken);
          sessionStorage.removeItem('verifyToken');
        }),
      );
  }

  getToken(): string | null {
    const token = sessionStorage.getItem('accessToken');

    return token;
  }

  logout(): void {
    sessionStorage.removeItem('accessToken');
    window.location.href = '/login';
  }

  logoutSso() {
    return this.http.patch(environment.api + `sso-auth-logout`, {
      observe: 'response',
    });
  }

  whoAmI(accessToken: string) {
    const headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${accessToken?.trim()}`,
    );
    return this.http
      .get<IUser>(this.whoAmiURL, {
        observe: 'response',
        headers,
      })
      .pipe(
        map((response: HttpResponse<IUser>) => {
          const user = response.body;
          this.user$.next(user);
          sessionStorage.setItem('mobile', user?.mobile ? user.mobile : '');
          sessionStorage.setItem('user', JSON.stringify(user));
          return response;
        }),
        catchError((error) => {
          return throwError(() => new Error(error));
        }),
      );
  }

  accountsCheck(payload: Partial<ICheckEmail>) {
    return this.http.post<ICheckEmail>(this.accountsCheckURL, payload, {
      observe: 'response',
    });
  }

  showToast(
    type: 'success' | 'error' | 'warning' | 'email' | 'copied',
    message: string,
    title?: string,
  ): void {
    const configMap = {
      success: {
        icon: 'check_circle',
        title: title || 'User Request approved!',
        panelClass: ['custom-snackbar-container', 'toast-success'],
      },
      error: {
        icon: 'cancel',
        title: title || 'User Request rejected!',
        panelClass: ['custom-snackbar-container', 'toast-error'],
      },
      warning: {
        icon: 'warning',
        title: 'User Request Error!',
        panelClass: ['custom-snackbar-container', 'toast-warning'],
      },
      email: {
        icon: 'check_circle',
        title: 'All Emails Sent!',
        panelClass: ['custom-snackbar-container', 'toast-success'],
      },
      copied: {
        icon: 'file_copy',
        title: 'Link Copied',
        panelClass: ['custom-snackbar-container', 'toast-success'],
      },
    };

    const config = configMap[type];

    this.snackBar.openFromComponent(CustomSnackbarComponent, {
      data: {
        icon: config.icon,
        title: config.title,
        message,
      },
      duration: 5000,
      panelClass: config.panelClass,
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }
  showhelpToast(type: 'success' | 'error', message: string): void {
    const configMap = {
      success: {
        icon: 'check_circle',
        title: ' Query Resolved!',
        panelClass: ['custom-snackbar-container', 'toast-success'],
      },
      error: {
        icon: 'cancel',
        title: 'Query Rejected!',
        panelClass: ['custom-snackbar-container', 'toast-error'],
      },
    };

    const config = configMap[type];

    this.snackBar.openFromComponent(CustomSnackbarComponent, {
      data: {
        icon: config.icon,
        title: config.title,
        message,
      },
      duration: 5000,
      panelClass: config.panelClass,
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }
  showAppointmentToast(type: 'success' | 'error', message: string): void {
    const configMap = {
      success: {
        icon: 'check_circle',
        title: ' Appointment Request approved!!',
        panelClass: ['custom-snackbar-container', 'toast-success'],
      },
      error: {
        icon: 'cancel',
        title: 'Appointment Request pending!',
        panelClass: ['custom-snackbar-container', 'toast-error'],
      },
    };

    const config = configMap[type];

    this.snackBar.openFromComponent(CustomSnackbarComponent, {
      data: {
        icon: config.icon,
        title: config.title,
        message,
      },
      duration: 5000,
      panelClass: config.panelClass,
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }
  showsubmitTost(type: 'success' | 'error', message: string): void {
    const configMap = {
      success: {
        icon: 'check_circle',
        title: ' Form Submitted!',
        panelClass: ['custom-snackbar-container', 'toast-success'],
      },
      error: {
        icon: 'cancel',
        title: 'Error!',
        panelClass: ['custom-snackbar-container', 'toast-error'],
      },
    };

    const config = configMap[type];

    this.snackBar.openFromComponent(CustomSnackbarComponent, {
      data: {
        icon: config.icon,
        title: config.title,
        message,
      },
      duration: 5000,
      panelClass: config.panelClass,
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }

  dynamicShowToast(
    type: 'success' | 'error' | 'warning',
    title: string,
    message: string,
  ): void {
    const iconMap = {
      success: 'check_circle',
      error: 'cancel',
      warning: 'warning',
    };

    const panelClassMap = {
      success: ['custom-snackbar-container', 'toast-success'],
      error: ['custom-snackbar-container', 'toast-error'],
      warning: ['custom-snackbar-container', 'toast-warning'],
    };

    this.snackBar.openFromComponent(CustomSnackbarComponent, {
      data: {
        icon: iconMap[type],
        title,
        message,
      },
      duration: 5000,
      panelClass: panelClassMap[type],
      horizontalPosition: 'end',
      verticalPosition: 'top',
    });
  }
}
